import React, { useContext, useMemo, createContext } from 'react';
import { UseQueryResult, useQuery } from '@edreamsodigeo/frontend-api-client';
import {
  SemItinerarySearchQuery,
  SemItinerarySearchQueryVariables,
  SemItinerariesSearchRequest,
} from 'generated/graphql';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';
import { SEM_ITINERARY_SEARCH_QUERY } from '../../operations/semItinerarySearchQuery';
import { useSemSearchResponseTrackErrors } from './hooks';
import { useEndSearchMetricEffect } from '../../SearchTrackerProvider';
import { useSemSearchQuery } from '../SemSearchRequestProvider';

export type SemSearchResultContextType = Partial<
  UseQueryResult<SemItinerarySearchQuery, SemItinerarySearchQueryVariables>
> & { error?: SemSearchResultContextType['errors'] };

const LOADING_STATE = { loading: true, data: null, error: null };
const SemSearchResponseContext =
  createContext<SemSearchResultContextType>(LOADING_STATE);

const useNextQueryState = (
  searchQueryState: UseQueryResult<
    SemItinerarySearchQuery,
    SemItinerarySearchQueryVariables
  >,
  loading: boolean
): SemSearchResultContextType => {
  return useMemo(
    () => ({
      ...searchQueryState,
      // TODO - This weird cast is due to the old graphql client, remove once we go with the new one
      error:
        searchQueryState.errors ||
        (
          searchQueryState as unknown as {
            error: SemSearchResultContextType['errors'];
          }
        ).error,
      loading,
      data: loading ? null : searchQueryState.data,
    }),
    [searchQueryState, loading]
  );
};

const SemSearchResponseProvider: React.FC = ({ children }) => {
  const semRequestVariables = useSemSearchQuery();
  const semQueryResult = useQuery<
    SemItinerarySearchQuery,
    SemItinerarySearchQueryVariables
  >(SEM_ITINERARY_SEARCH_QUERY, {
    variables: {
      semItinerariesSearchRequest:
        semRequestVariables as SemItinerariesSearchRequest,
    },
    skip: !window.location.href.includes('bestprices') || !semRequestVariables,
  });
  // This sets the loading flag if we're not in the page because otherwise
  // all the parsers will run and throw an error
  const isLoading =
    semQueryResult.loading ||
    !window.location.href.includes('bestprices') ||
    !semRequestVariables;
  const semQueryResultWithError = useNextQueryState(semQueryResult, isLoading);

  useEndSearchMetricEffect(semQueryResult);
  useFrontendApiBlockingError(semQueryResult);
  useSemSearchResponseTrackErrors(semQueryResult);

  return (
    <SemSearchResponseContext.Provider value={semQueryResultWithError}>
      {children}
    </SemSearchResponseContext.Provider>
  );
};

export const useSemSearchResponseQuery = (): SemSearchResultContextType =>
  useContext(SemSearchResponseContext);

export const useSemDefaultFeeType = ():
  | SemItinerarySearchQuery['semItinerarySearch']['priceTypeDisplayed']
  | null => {
  const { errors, loading, data } = useSemSearchResponseQuery();
  if (errors || loading) {
    return null;
  }
  return data?.semItinerarySearch?.priceTypeDisplayed || null;
};

export default SemSearchResponseProvider;
